import { buildingUsageCategoriesSchema } from "./building-usage.js";
import { FormatRegistry, Type } from "@sinclair/typebox";
// Value.Check shares these contracts outside Fastify's AJV validator.
if (!FormatRegistry.Has("uuid")) {
    FormatRegistry.Set("uuid", (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value));
}
const uuid = Type.String({ format: "uuid" });
const decimal = Type.String({ pattern: "^\\d+(?:\\.\\d{1,4})?$" });
const textList = Type.Array(Type.String());
export const campaignValueStatusSchema = Type.Union([
    Type.Literal("known"),
    Type.Literal("unknown"),
    Type.Literal("not_applicable")
]);
// Optional only for stored v1 replay compatibility. v2 import/save emits all states.
export const campaignLineEvidenceProperties = {
    quantityStatus: Type.Optional(campaignValueStatusSchema),
    priceStatus: Type.Optional(campaignValueStatusSchema),
    quantityReason: Type.Optional(Type.String({ maxLength: 2000 })),
    priceReason: Type.Optional(Type.String({ maxLength: 2000 })),
    costStatus: Type.Optional(Type.Union([
        Type.Literal("priced"),
        Type.Literal("unpriced"),
        Type.Literal("included"),
        Type.Literal("not_applicable")
    ])),
    includedInLineKey: Type.Optional(Type.Union([Type.String({ minLength: 1, maxLength: 120 }), Type.Null()])),
    sourceIds: Type.Optional(Type.Array(Type.String({ minLength: 1 }))),
    origin: Type.Optional(Type.Object({
        kind: Type.Union([Type.Literal("dossier"), Type.Literal("replay"), Type.Literal("manual")]),
        sourceLineKey: Type.String({ minLength: 1, maxLength: 120 }),
        sourceDigest: Type.Optional(Type.String({ minLength: 1 })),
        catalogBindings: Type.Optional(Type.Array(Type.Object({
            id: uuid,
            code: Type.String(),
            versionId: uuid,
            unit: Type.String(),
            unitPrice: Type.Union([decimal, Type.Null()])
        }, { additionalProperties: false })))
    }, { additionalProperties: false }))
};
export const campaignEconomicSummaryProperties = {
    knownSubtotal: Type.Optional(decimal),
    completeNetTotal: Type.Optional(Type.Union([decimal, Type.Null()])),
    pricingComplete: Type.Optional(Type.Boolean()),
    scopeComplete: Type.Optional(Type.Boolean())
};
export const campaignScenarioTypeSchema = Type.Union([
    Type.Literal("pv_only"),
    Type.Literal("roof_and_pv")
]);
export const campaignSourceSchema = Type.Object({
    id: Type.String(),
    title: Type.String(),
    url: Type.Union([Type.String(), Type.Null()]),
    accessMethod: Type.String(),
    fetchedAt: Type.Union([Type.String(), Type.Null()]),
    documentDate: Type.Union([Type.String(), Type.Null()]),
    excerpt: Type.String(),
    status: Type.String(),
    limitations: textList
});
export const campaignScenarioLineSchema = Type.Object({
    ...campaignLineEvidenceProperties,
    lineKey: Type.Optional(Type.String({ minLength: 1, maxLength: 120 })),
    description: Type.String(),
    unit: Type.String(),
    quantity: Type.Union([decimal, Type.Null()]),
    unitPrice: Type.Union([decimal, Type.Null()]),
    amount: Type.Union([decimal, Type.Null()]),
    referenceCode: Type.String()
});
export const campaignScenarioSchema = Type.Object({
    ...campaignEconomicSummaryProperties,
    scenarioType: campaignScenarioTypeSchema,
    label: Type.String(),
    footprintM2: decimal,
    utilizationPct: Type.Literal(50),
    usableAreaM2: decimal,
    moduleCount: Type.Integer({ minimum: 0 }),
    capacityKwp: decimal,
    moduleSubtotalEur: decimal,
    roofSubtotalEur: decimal,
    netAmountEur: decimal,
    vat: Type.Null(),
    totalIncludingVat: Type.Null(),
    partialOffer: Type.Literal(true),
    lines: Type.Array(campaignScenarioLineSchema)
});
export const campaignComparisonSchema = Type.Object({
    comparisonId: Type.String(),
    scenarios: Type.Tuple([campaignScenarioSchema, campaignScenarioSchema]),
    assumptions: textList,
    limitations: textList,
    summary: Type.String(),
    candidateCompanyId: Type.Union([Type.String(), Type.Null()]),
    companyAtAddressSourceIds: textList,
    ownership: Type.Literal("unknown"),
    occupancy: Type.Literal("unknown"),
    sourceIds: textList,
    sourceDigests: Type.Record(Type.String(), Type.String()),
    catalogDigest: Type.String(),
    partialOffer: Type.Literal(true),
    vat: Type.Null(),
    totalIncludingVat: Type.Null()
});
export const campaignCreateRequestSchema = Type.Object({
    usageCategories: Type.Optional(buildingUsageCategoriesSchema),
    includeEstimatedUsage: Type.Optional(Type.Boolean()),
    idempotencyKey: uuid,
    title: Type.Optional(Type.String({ minLength: 1, maxLength: 160 })),
    minFootprintM2: Type.Number({ minimum: 2000, maximum: 1000000 }),
    maxBuildings: Type.Integer({ minimum: 1, maximum: 8 }),
    budgetMicrousd: Type.Integer({ minimum: 1, maximum: 10000000 })
}, { additionalProperties: false });
export const campaignParametersSchema = Type.Object({
    usageCategories: Type.Optional(buildingUsageCategoriesSchema),
    includeEstimatedUsage: Type.Optional(Type.Boolean()),
    bbox: Type.Tuple([Type.Number(), Type.Number(), Type.Number(), Type.Number()]),
    municipalityCode: Type.String(),
    minFootprintM2: Type.Number(),
    maxBuildings: Type.Integer(),
    budgetMicrousd: Type.Integer(),
    utilizationPct: Type.Literal(50)
});
export const campaignStateSchema = Type.Union([
    Type.Literal("queued"),
    Type.Literal("running"),
    Type.Literal("completed"),
    Type.Literal("partial"),
    Type.Literal("failed"),
    Type.Literal("cancelled")
]);
const summaryProperties = {
    id: uuid,
    title: Type.String(),
    state: campaignStateSchema,
    createdAt: Type.String(),
    updatedAt: Type.String(),
    parameters: campaignParametersSchema,
    selectedCount: Type.Integer(),
    readyCount: Type.Integer(),
    issueCount: Type.Integer(),
    spentMicrousd: Type.Integer(),
    reservedMicrousd: Type.Integer(),
    mode: Type.Literal("replay"),
    paidProvidersEnabled: Type.Literal(false)
};
export const campaignSummarySchema = Type.Object(summaryProperties);
export const campaignItemSchema = Type.Object({
    id: uuid,
    structureId: uuid,
    label: Type.String(),
    footprintM2: decimal,
    center: Type.Tuple([Type.Number(), Type.Number()]),
    state: Type.Union([
        Type.Literal("queued"),
        Type.Literal("running"),
        Type.Literal("ready"),
        Type.Literal("insufficient_data"),
        Type.Literal("failed"),
        Type.Literal("budget_blocked"),
        Type.Literal("cancelled")
    ]),
    stage: Type.String(),
    errorCode: Type.Optional(Type.String()),
    sources: Type.Array(campaignSourceSchema),
    comparison: Type.Union([campaignComparisonSchema, Type.Null()])
});
export const campaignReportSchema = Type.Object({
    eligibleCount: Type.Integer(),
    selectedCount: Type.Integer(),
    excludedCount: Type.Integer(),
    readyCount: Type.Integer(),
    missingCount: Type.Integer(),
    failedCount: Type.Integer(),
    cancelledCount: Type.Integer(),
    budgetBlockedCount: Type.Integer(),
    summary: Type.String()
});
export const campaignDetailSchema = Type.Object({
    ...summaryProperties,
    items: Type.Array(campaignItemSchema),
    report: Type.Union([campaignReportSchema, Type.Null()])
});
export const campaignListSchema = Type.Object({ items: Type.Array(campaignSummarySchema) });
export const campaignOpenQuoteRequestSchema = Type.Object({ scenarioType: campaignScenarioTypeSchema }, { additionalProperties: false });
export const campaignOpenQuoteResponseSchema = Type.Object({ bundleId: uuid, workItemId: uuid });
//# sourceMappingURL=campaigns.js.map